import { cn } from '@/lib/utils';

/**
 * PatientRecord Icon Component
 *
 * A customizable patient record/document icon
 *
 * @example
 * ```tsx
 * <PatientRecord />
 * <PatientRecord className="text-blue-500" size={24} />
 * <PatientRecord fill="currentColor" width={16} height={18} />
 * ```
 */

export interface PatientRecordProps extends React.SVGProps<SVGSVGElement> {
  /**
   * Size of the icon (sets both width and height)
   * @default 24
   */
  size?: number;
  /**
   * Custom width (overrides size)
   */
  width?: number;
  /**
   * Custom height (overrides size)
   */
  height?: number;
  /**
   * Fill color for the icon
   * Use "currentColor" to inherit text color from parent
   * @default "currentColor"
   */
  fill?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
}

export function PatientRecord({
  size = 24,
  width,
  height,
  fill = 'currentColor',
  className,
  ...props
}: PatientRecordProps) {
  const w = width ?? size;
  const h = height ?? size; // Square aspect ratio

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={w}
      height={h}
      viewBox="0 0 24 24"
      fill="none"
      className={cn('inline-block flex-shrink-0', className)}
      aria-hidden="true"
      {...props}
    >
      <path
        d="M18.5 23H4.5C3.4 23 2.5 22.1 2.5 21V7H4.5V21H18.5V23ZM15 7V5H13V7H11V9H13V11H15V9H17V7H15ZM17 13H11V15H17V13ZM15.5 1H8.5C7.4 1 6.51 1.9 6.51 3L6.5 17C6.5 18.1 7.39 19 8.49 19H19.5C20.6 19 21.5 18.1 21.5 17V7L15.5 1ZM19.5 17H8.5V3H14.67L19.5 7.83V17Z"
        fill={fill}
      />
    </svg>
  );
}
